/**
 * `/verify-email`
 *
 * The screen the emailed verification link lands on. It POSTs the token from the query string.
 *
 * Verification runs once on mount, guarded by a ref rather than left to the effect alone: React's
 * StrictMode double-invokes effects in development, and a second POST would consume an
 * already-spent token and render a spurious failure over a successful verification.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';

import { AuthLayout } from '../../components/composites/AuthLayout';
import { Notice } from '../../components/primitives';
import { ApiError, verifyEmail } from './api';
import { ME_QUERY_KEY } from './useAuth';

export function VerifyEmailScreen(): React.JSX.Element {
  const { t } = useTranslation('common');
  const search = useSearch({ from: '/verify-email' });
  const queryClient = useQueryClient();
  const attempted = useRef(false);

  const mutation = useMutation({
    mutationFn: (token: string) => verifyEmail(token),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    },
  });

  const { mutate } = mutation;
  const token = search.token;

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    mutate(token);
  }, [token, mutate]);

  const error = mutation.error instanceof ApiError ? mutation.error : undefined;

  return (
    <AuthLayout eyebrow="PIUS SELECTIONS" heading="Email verification">
      {!token ? (
        <Notice>That link is missing its token.</Notice>
      ) : mutation.isPending ? (
        <p style={{ fontSize: 13.5 }}>Verifying…</p>
      ) : error ? (
        <Notice referenceId={error.correlationId}>{error.message}</Notice>
      ) : mutation.isSuccess ? (
        <Notice tone="success">Your address is verified.</Notice>
      ) : null}

      <p style={{ fontSize: 13, marginTop: 18 }}>
        <Link to="/">{t('created.continue_to_liturgy', 'Continue')}</Link>
      </p>
    </AuthLayout>
  );
}
